import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  faArrowLeftFromBracket,
  faBars,
  faCircleQuestion,
  faClockRotateLeft,
  faSliders
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { AllCampaignsIcon, AllLineItemsIcon, AllPlatformBuysIcon } from './nav-icons';
import { BravoLogo } from '../display/logo';
import { useAdminEnabled } from '@/features/admin/admin-section';
import { useFlagEnabled, useJiraSupportEnabled, useWrkthruEnabled } from '@/utils/feature-flags';
import { WrkthruMark } from '@/features/wrkthru/wrkthru-mark';
import { supportPortalUrl } from '@/utils/support-url';

export function Sidebar() {
  const [expanded, setExpanded] = React.useState(false);
  const [transitionDone, setTransitionDone] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const adminVisible = useAdminEnabled();
  const activityEnabled = useFlagEnabled('activity-log-v1');
  const wrkthruEnabled = useWrkthruEnabled();
  const jiraSupportEnabled = useJiraSupportEnabled();

  useEffect(() => {
    const handleTransitionEnd = () => setTransitionDone(true);

    const element = containerRef.current;
    if (element) element.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      if (element) element.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, []);

  function toggleExpanded() {
    setExpanded(!expanded);
    setTransitionDone(false);
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'transform border-r border-gray-200 bg-white shadow-xl transition-all duration-300 ease-in-out',
        expanded ? 'w-[250px]' : 'w-[70px]'
      )}>
      <div className="flex h-full flex-col items-center py-8">
        <MenuItem
          icon={<FontAwesomeIcon className="cursor-pointer text-xl" icon={faBars} />}
          onClick={toggleExpanded}
          showLabel={transitionDone && expanded}
          label={<BravoLogo />}
        />
        <div className="h-16" />
        {wrkthruEnabled ? (
          <>
            <div className="h-4" />
            <MenuItem
              to="/wrkthru"
              icon={<WrkthruMark size={24} />}
              label={<MenuText label="Wrkthru" />}
              showLabel={transitionDone && expanded}
            />
          </>
        ) : null}
        <div className="h-4" />
        <MenuItem
          to="/campaigns"
          icon={<AllCampaignsIcon />}
          label={<MenuText label="Campaigns" />}
          showLabel={transitionDone && expanded}
        />
        <div className="h-4" />
        <MenuItem
          to="/line-items"
          icon={<AllLineItemsIcon />}
          label={<MenuText label="Line Items" />}
          showLabel={transitionDone && expanded}
        />
        <div className="h-4" />
        <MenuItem
          to="/media-buys"
          icon={<AllPlatformBuysIcon />}
          label={<MenuText label="Platform Buys" />}
          showLabel={transitionDone && expanded}
        />
        {activityEnabled ? (
          <>
            <div className="h-4" />
            <MenuItem
              to="/activity"
              icon={<FontAwesomeIcon className="cursor-pointer text-xl" icon={faClockRotateLeft} />}
              label={<MenuText label="Activity" />}
              showLabel={transitionDone && expanded}
            />
          </>
        ) : null}
        <div className="flex-1" />
        {jiraSupportEnabled && (
          <MenuItem
            href={supportPortalUrl()}
            external
            icon={<FontAwesomeIcon className="cursor-pointer text-xl" icon={faCircleQuestion} />}
            label={<MenuText label="Support" />}
            showLabel={transitionDone && expanded}
          />
        )}
        {adminVisible && (
          <MenuItem
            to={'/admin'}
            icon={<FontAwesomeIcon className="cursor-pointer text-xl" icon={faSliders} />}
            label={<MenuText label="Admin" />}
            showLabel={transitionDone && expanded}
          />
        )}
        <MenuItem
          href="/auth/logout"
          icon={
            <FontAwesomeIcon className="cursor-pointer text-xl" icon={faArrowLeftFromBracket} />
          }
          label="Logout"
          showLabel={transitionDone && expanded}
        />
      </div>
    </div>
  );
}

type MenuItemProps = {
  icon: React.ReactNode;
  label?: React.ReactNode;
  showLabel?: boolean;
  to?: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
};

function MenuItem({ icon, label, showLabel, to, href, external, onClick }: MenuItemProps) {
  const content = (
    <>
      <div className="flex w-[70px] justify-center">
        <div className="h-6 w-6">{icon}</div>
      </div>
      {label && showLabel && label}
    </>
  );

  const className = 'flex h-[48px] w-full cursor-pointer items-center no-underline text-inherit';

  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={className}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer' : undefined}>
        {content}
      </a>
    );
  }

  return (
    <div className={className} onClick={onClick}>
      {content}
    </div>
  );
}

export function MenuText({ label, classes }: { label: string; classes?: string }) {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  // Trigger the opacity transition when the component mounts
  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 20);
    return () => clearTimeout(timeout); // Cleanup timeout if component unmounts
  }, []);

  return (
    <div
      className={clsx(
        'text-[#374151] transition-opacity duration-100',
        classes,
        isVisible ? 'opacity-100' : 'opacity-0'
      )}>
      {label}
    </div>
  );
}
